import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";

export type CartItem = {
  id: string;
  productId: string;
  name: string;
  slug: string;
  weight: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  imageUrl: string | null;
};

export type CartView = {
  cartId: string;
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  subtotalPaise: number;
  checkout: {
    allowed: boolean;
    qualifiesFreeShipping: boolean;
    message: string | null;
    freeShippingAbovePaise: number;
    shippingFlatPaise: number;
    shippingPaise: number;
    remainingForFreeShippingPaise: number;
  };
};

type CartContextValue = {
  cart: CartView | undefined;
  loading: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  addItem: (productId: string, quantity?: number) => Promise<void>;
  updateQty: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  refresh: () => Promise<void>;
};

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const cartQuery = useQuery({
    queryKey: ["cart", user?.id ?? "guest"],
    queryFn: () =>
      api<CartView>("/api/cart", { token: accessToken ?? undefined }),
  });

  useEffect(() => {
    void qc.invalidateQueries({ queryKey: ["cart"] });
  }, [user?.id, qc]);

  const addMutation = useMutation({
    mutationFn: (input: { productId: string; quantity: number }) =>
      api<CartView>("/api/cart/items", {
        method: "POST",
        token: accessToken ?? undefined,
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      qc.setQueryData(["cart", user?.id ?? "guest"], data);
      setOpen(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: { itemId: string; quantity: number }) =>
      api<CartView>(`/api/cart/items/${input.itemId}`, {
        method: "PATCH",
        token: accessToken ?? undefined,
        body: JSON.stringify({ quantity: input.quantity }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(["cart", user?.id ?? "guest"], data);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (itemId: string) =>
      api<CartView>(`/api/cart/items/${itemId}`, {
        method: "DELETE",
        token: accessToken ?? undefined,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["cart", user?.id ?? "guest"], data);
    },
  });

  const addItem = useCallback(
    async (productId: string, quantity = 1) => {
      await addMutation.mutateAsync({ productId, quantity });
    },
    [addMutation],
  );

  const updateQty = useCallback(
    async (itemId: string, quantity: number) => {
      await updateMutation.mutateAsync({ itemId, quantity });
    },
    [updateMutation],
  );

  const removeItem = useCallback(
    async (itemId: string) => {
      await removeMutation.mutateAsync(itemId);
    },
    [removeMutation],
  );

  const refresh = useCallback(async () => {
    await cartQuery.refetch();
  }, [cartQuery]);

  const value = useMemo<CartContextValue>(
    () => ({
      cart: cartQuery.data,
      loading: cartQuery.isLoading,
      open,
      setOpen,
      addItem,
      updateQty,
      removeItem,
      refresh,
    }),
    [
      cartQuery.data,
      cartQuery.isLoading,
      open,
      addItem,
      updateQty,
      removeItem,
      refresh,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
